import { useMemo } from "react";
import { Box } from "@mantine/core";
import type {
  ReportSupplementEntry,
  ReportSupplementNoticeFragmentConfig,
} from "../../types/reportTemplate";
import { buildReportSupplementNoticeFragments } from "./reportSupplementNoticeFragments";
import { selectSupplementNoticeFragments } from "./reportSupplementNoticeSections";
import { ReportAmendmentEntry } from "./ReportAmendmentEntry";
import { ReportCorrectionEntry } from "./ReportCorrectionEntry";
import { ReportNotificationBanner } from "./ReportNotificationBanner";
import {
  groupReportSupplements,
  type ReportSupplementGroupType,
} from "./reportSupplementGroups";

interface ReportSupplementNoticesProps {
  entries: ReportSupplementEntry[];
  fragment?: ReportSupplementNoticeFragmentConfig;
  correctionPlacement?: "banner" | "details";
}

const BANNER_LABELS: Record<ReportSupplementGroupType, string> = {
  addendum: "ADDENDUM ATTACHED",
  amended: "AMENDED REPORT",
  corrected: "CORRECTED REPORT",
};

export function ReportSupplementNotices({
  entries,
  fragment,
  correctionPlacement,
}: ReportSupplementNoticesProps) {
  const groups = useMemo(
    () =>
      (fragment ? [] : groupReportSupplements(entries)).filter(
        (group) =>
          correctionPlacement !== "details" ||
          group.type === "corrected" ||
          group.type === "amended"
      ),
    [entries, fragment, correctionPlacement]
  );
  const orderedFragments = useMemo(
    () =>
      selectSupplementNoticeFragments(
        {
          type: "report_supplement_notices",
          label: "",
          config: { correctionPlacement },
        },
        buildReportSupplementNoticeFragments(groups)
      ),
    [correctionPlacement, groups]
  );
  const fragmentEntry = useMemo(
    () =>
      fragment?.groupType === "amended" || fragment?.groupType === "corrected"
        ? entries.find((entry) => entry.id === fragment.entryId)
        : undefined,
    [entries, fragment]
  );

  if (fragment) {
    return (
      <Box data-report-supplement-notices="true">
        <Box data-report-supplement-group={fragment.groupType}>
          {fragment.showBanner && correctionPlacement !== "details" && (
            <ReportNotificationBanner
              label={BANNER_LABELS[fragment.groupType]}
            />
          )}
          {fragment.groupType === "amended" &&
            fragmentEntry &&
            correctionPlacement !== "banner" && (
              <ReportAmendmentEntry
                entry={fragmentEntry}
                showDivider={fragment.showDivider}
                fragment={fragment}
              />
            )}
          {fragment.groupType === "corrected" &&
            fragmentEntry &&
            correctionPlacement !== "banner" && (
              <ReportCorrectionEntry
                entry={fragmentEntry}
                showDivider={fragment.showDivider}
                fragment={fragment}
              />
            )}
        </Box>
      </Box>
    );
  }

  if (groups.length === 0) return null;

  return (
    <Box data-report-supplement-notices="true">
      {orderedFragments.map((item, index) => (
        <ReportSupplementNotices
          key={`${
            ("entryId" in item && item.entryId) || item.groupType
          }:${index}`}
          entries={entries}
          fragment={item}
          correctionPlacement={correctionPlacement}
        />
      ))}
    </Box>
  );
}
